// v2.18.5 — Watchdog anti-embouteillage (passif, sans API trafic externe).
// Écoute les événements `civ-courier-waypoint` émis par useCoursePinger.
// Fenêtre glissante 3 waypoints. Trigger si vitesse moyenne < 5 km/h sur ≥ 60s
// ET dist restante > 1km. Anti-spam : cooldown 5 min entre 2 triggers.
// Sur trigger → POST /api/v3/courier/routes/alternatives → onTrafficDetected.
//
// IMPORTANT design : on lit toutes les props courantes via une ref synchrone pour
// éviter que le re-render parent ne ré-installe le listener (perte d'événements).

import { useEffect, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const TRIGGER_SPEED_KMH = 5;
const TRIGGER_DURATION_S = 60;
const COOLDOWN_MS = 5 * 60 * 1000;
const MIN_REMAINING_KM = 1;
const WINDOW_SIZE = 3;

function _haversineKm(a, b) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

export default function useTrafficWatchdog({
  enabled = false,
  destination = null,
  currentEtaMin = null,
  onTrafficDetected = null,
}) {
  // Ref synchrone des paramètres courants — évite les re-runs du useEffect.
  const paramsRef = useRef({ enabled, destination, currentEtaMin, onTrafficDetected });
  useEffect(() => {
    paramsRef.current = { enabled, destination, currentEtaMin, onTrafficDetected };
  });

  const waypointsRef = useRef([]); // [{lat, lng, ts}]
  const lastTriggerRef = useRef(0);
  const inflightRef = useRef(false);

  useEffect(() => {
    const handleWaypoint = async (ev) => {
      const wp = ev?.detail;
      if (!wp || !Number.isFinite(wp.lat) || !Number.isFinite(wp.lng)) return;

      const { enabled: en, destination: dest, currentEtaMin: ceta, onTrafficDetected: cb } = paramsRef.current;
      if (!en || !dest) return;

      const w = [...waypointsRef.current, wp].slice(-WINDOW_SIZE);
      waypointsRef.current = w;

      if (w.length < 2) return;

      if (Date.now() - lastTriggerRef.current < COOLDOWN_MS) return;

      let totalKm = 0;
      for (let i = 1; i < w.length; i++) totalKm += _haversineKm(w[i - 1], w[i]);
      const dtSec = (w[w.length - 1].ts - w[0].ts) / 1000;
      if (dtSec < TRIGGER_DURATION_S) return;
      const avgSpeedKmh = (totalKm / dtSec) * 3600;

      const remainingKm = _haversineKm(w[w.length - 1], dest);
      if (remainingKm < MIN_REMAINING_KM) return;

      if (avgSpeedKmh > TRIGGER_SPEED_KMH) return;

      if (inflightRef.current) return;
      inflightRef.current = true;

      // ETA réaliste basé sur vitesse observée (livreur bloqué).
      const jamEtaMin = Math.max(3, Math.round((remainingKm / Math.max(1, avgSpeedKmh)) * 60));
      const sentEtaMin = ceta && ceta > jamEtaMin ? ceta : jamEtaMin;

      try {
        const r = await axios.post(
          `${API}/v3/courier/routes/alternatives`,
          {
            current_lat: w[w.length - 1].lat,
            current_lng: w[w.length - 1].lng,
            destination: { lat: dest.lat, lng: dest.lng },
            current_route_remaining_minutes: sentEtaMin,
          },
          { withCredentials: true },
        );
        if (r.data?.ok && r.data.best_recommended_id && typeof cb === "function") {
          lastTriggerRef.current = Date.now();
          const best = r.data.alternatives.find((a) => a.id === r.data.best_recommended_id);
          cb({
            best,
            alternatives: r.data.alternatives,
            current: r.data.current,
            avg_speed_kmh: +avgSpeedKmh.toFixed(1),
          });
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[useTrafficWatchdog] alternatives call failed:", e.response?.data?.error || e.message);
      } finally {
        inflightRef.current = false;
      }
    };

    window.addEventListener("civ-courier-waypoint", handleWaypoint);
    return () => window.removeEventListener("civ-courier-waypoint", handleWaypoint);
    // Listener installé UNE SEULE FOIS au mount. Les valeurs courantes sont lues
    // via paramsRef qui est synchronisée à chaque render via l'autre useEffect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Permet de forcer un snooze (ex: user click "Non, je continue").
  const snooze = () => { lastTriggerRef.current = Date.now(); };

  return { snooze };
}
